import { useState } from "react";
import toast from "react-hot-toast";

import PartnerAdminForm from "./PartnerAdminForm";
import ButtonLoader from "../ButtonLoader";
import DataTable from "react-data-table-component";
import ViewSection from "../ViewSection";
import TableLoader from "../TableLoader";

import usePartnerCompanyProjects from "../../hooks/usePartnerCompanyProjects";

import partnerCompanyService from "../../services/partnerCompanyService";

const PartnerCompanyView = ({ company, mutateCompanies, onCloseClicked }) => {
  const [isInviteAdminOpen, setIsInviteAdminOpen] = useState(false);

  const [isSubmittingInviteAdmin, setIsSubmittingInviteAdmin] = useState(false);
  const [isRemovingAdmin, setIsRemovingAdmin] = useState(false);

  const { data: projectsData, isLoading: isLoadingProjects } = usePartnerCompanyProjects({
    partnerCompanyId: company._id,
    page: 1,
    perPage: 99999
  });

  const projects = projectsData?.projects || [];

  const handleInviteAdmin = async ({ fullName, email }) => {
    setIsSubmittingInviteAdmin(true);

    try {
      const { partnerCompany, admin } = await partnerCompanyService.inviteAdmin({
        partnerCompanyId: company._id,
        fullName,
        email
      });

      company.admin = admin;

      await mutateCompanies();

      setIsInviteAdminOpen(false);

      toast.success(`${admin.fullName} invited to ${partnerCompany.name}`);
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    } finally {
      setIsSubmittingInviteAdmin(false);
    }
  }

  const handleRemoveAdmin = async () => {
    try {
      setIsRemovingAdmin(true);

      await partnerCompanyService.removeAdmin(company._id);

      company.admin = null;

      await mutateCompanies();

      toast.success(`Admin removed from ${company.name}`);
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    } finally {
      setIsRemovingAdmin(false);
    }
  }

  return (
    <div className="bg-white rounded-lg border border-ase-blue w-full md:w-auto md:min-w-[700px]">
      <div className="flex items-center justify-between text-lg bg-ase-blue text-white p-4 font-bold rounded-t-md">
        <p>{company.name}</p>

        <p
          onClick={onCloseClicked}
          className="font-bold cursor-pointer text-2xl"
        >
          X
        </p>
      </div>

      <div className="flex flex-col gap-2 p-4">
        <p><b className="inline-block min-w-[150px]">Company name:</b>{company.name}</p>
        <p><b className="inline-block min-w-[150px]">Company email:</b>{company.email}</p>
      </div>

      <ViewSection
        title="Projects"
        expandable={true}
        content={(
          <DataTable
            noHeader
            columns={[
              {
                name: 'Quote #',
                selector: row => row.aseId,
                sortable: true,
                sortField: 'aseId'
              },
              {
                name: 'Name',
                selector: row => row.name,
                sortable: true,
                sortField: 'name'
              },
              {
                name: 'Client',
                selector: row => row.client,
                sortable: true,
                sortField: 'description'
              }
            ]}
            progressPending={isLoadingProjects}
            progressComponent={<TableLoader />}
            data={projects}
            pagination={true}
            paginationPerPage={5}
            paginationRowsPerPageOptions={[5, 10, 15, 20]}
          />
        )}
      />

      <ViewSection
        title="Admin"
        expandable={true}
        content={(
          <div className="flex flex-col gap-4 p-4">
            {company.admin && (
              <div className="flex flex-col gap-2">
                <p>
                  <b className="inline-block min-w-[150px]">Admin name:</b>
                  {company.admin.fullName}
                </p>

                <p>
                  <b className="inline-block min-w-[150px]">Admin email:</b>
                  {company.admin.email}
                </p>

                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => setIsInviteAdminOpen(true)}
                    className="ase-button-primary px-2 py-1 text-sm"
                  >
                    Invite new
                  </button>

                  <button
                    onClick={handleRemoveAdmin}
                    className="ase-button-danger px-2 py-1 text-sm"
                  >
                    {isRemovingAdmin ? <ButtonLoader /> : "Remove"}
                  </button>
                </div>
              </div>
            )}

            {!company.admin && (
              <p>
                <b className="inline-block min-w-[75px]">Admin:</b>

                {!company.admin && !isInviteAdminOpen && (
                  <button
                    className="ase-button-primary px-2 py-1 text-sm"
                    onClick={() => setIsInviteAdminOpen(true)}
                  >
                    Invite
                  </button>
                )}
              </p>
            )}

            {isInviteAdminOpen && <PartnerAdminForm
              isLoading={isSubmittingInviteAdmin}
              onSubmit={handleInviteAdmin}
              onClose={setIsInviteAdminOpen}
            />
            }
          </div>
        )}
      />

      <ViewSection
        title="Staff"
        expandable={true}
        isFinalSection={true}
        content={
          <DataTable
            noHeader
            columns={[
              {
                name: 'Name',
                selector: row => row.fullName,
                sortable: true,
                sortField: 'fullName'
              },
              {
                name: 'Email',
                selector: row => row.email,
                sortable: true,
                sortField: 'email',
                grow: 2
              }
            ]}
            data={company.staff}
            pagination={true}
            paginationPerPage={5}
            paginationRowsPerPageOptions={[5, 10, 15, 20]}
          />
        }
      />
    </div>
  )
}

export default PartnerCompanyView;
